import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cartsBadge, type ShellTab } from "../shell/shell-tabs";

function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** iOS's SF Symbols for the tabs, drawn to their shapes: person.2, rectangle.on.rectangle.angled,
 * dot.radiowaves.left.and.right, shippingbox, ticket. */
const GLYPHS: Record<ShellTab, ReactNode> = {
  customer: (
    <Glyph>
      <circle cx="9" cy="8" r="3.25" />
      <path d="M3.5 19.5c0-3 2.5-5.25 5.5-5.25s5.5 2.25 5.5 5.25" />
      <path d="M15.5 4.9a3.25 3.25 0 0 1 0 6.2" />
      <path d="M17.5 14.6c1.8.7 3 2.5 3 4.9" />
    </Glyph>
  ),
  inventory: (
    <Glyph>
      <rect x="8.5" y="3.5" width="10" height="14" rx="2" />
      <path d="M5.6 7.4l-.9.3a2 2 0 0 0-1.3 2.5l3 9.5a2 2 0 0 0 2.5 1.3l5.7-1.8" />
    </Glyph>
  ),
  live: (
    <Glyph>
      <circle cx="12" cy="12" r="1.75" fill="currentColor" />
      <path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8" />
    </Glyph>
  ),
  shipping: (
    <Glyph>
      <path d="M3.5 7.5L12 3.5l8.5 4v9L12 20.5l-8.5-4z" />
      <path d="M3.5 7.5L12 11.5l8.5-4M12 11.5v9M7.75 5.5l8.5 4" />
    </Glyph>
  ),
  shows: (
    <Glyph>
      <path d="M4 6.5h16v3a2.5 2.5 0 0 0 0 5v3H4v-3a2.5 2.5 0 0 0 0-5z" />
      <path d="M14.5 7v1.5M14.5 11.25v1.5M14.5 15.5V17" />
    </Glyph>
  ),
};

const TITLES: Record<ShellTab, string> = {
  customer: "Customer",
  inventory: "Inventory",
  live: "Live",
  shipping: "Shipping",
  shows: "Shows",
};

/**
 * A phone's tabs along the bottom, as iOS's shop mode: a glyph over its word, the lit one in the accent and the rest
 * faint, the whole column the target. Customer carries the open carts' count; Live a pulsing dot while this device is
 * on air or the shop has a show live.
 */
export function TabBar({
  tabs,
  current,
  hrefOf,
  openCarts,
  live,
}: {
  tabs: readonly ShellTab[];
  current: ShellTab | null;
  hrefOf: (tab: ShellTab) => string;
  openCarts: number;
  live: "on-air" | "live" | null;
}) {
  const badge = cartsBadge(openCarts);
  return (
    <nav
      aria-label="Sections"
      className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-app-elevated pb-[env(safe-area-inset-bottom)] sm:hidden"
    >
      {/* In px, as iOS's bar: it stays 50 px at any text size the browser is set to, so no word of it is cut. */}
      <div className="flex h-[50px] items-stretch pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)]">
        {tabs.map((tab) => {
          const lit = tab === current;
          return (
            <Link
              key={tab}
              to={hrefOf(tab)}
              aria-current={lit ? "page" : undefined}
              className={`flex min-w-0 flex-1 flex-col items-center gap-[4px] pt-[8px] pb-[4px] transition-colors ${
                lit ? "text-accent" : "text-faint hover:text-muted"
              }`}
            >
              <span className="relative flex h-[22px] items-center">
                {GLYPHS[tab]}
                {tab === "customer" && badge && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-[7px] left-[calc(50%+3px)] flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-accent px-[4px] text-[10px] font-bold text-ink"
                  >
                    {badge}
                  </span>
                )}
                {tab === "live" && live && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-[2px] -right-[6px] size-[8px] animate-pulse rounded-full bg-danger motion-reduce:animate-none"
                  />
                )}
              </span>
              <span className="truncate text-[10px] leading-[12px] font-medium">{TITLES[tab]}</span>
              {tab === "customer" && badge && (
                <span className="sr-only">
                  , {badge} open {openCarts === 1 ? "cart" : "carts"}
                </span>
              )}
              {tab === "live" && live && (
                <span className="sr-only">{live === "on-air" ? ", you are on air" : ", a show is live"}</span>
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
